// src/pages/document/DocumentTypeListPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useDocumentDocumentType, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

/**
 * Document type list page.
 */
export const DocumentTypeListPage = observer(() => {
  const store = useDocumentDocumentType();
  const { t } = useI18n();
  const [search, setSearch] = useState('');

  useEffect(() => {
    store.ListDocumentTypes({ pagination: { limit: 100 } } as any);
  }, []);

  const visibleItems = store.items.filter((item: any) =>
    `${item.display_name || ''} ${item.slug || ''}`.toLocaleLowerCase().includes(search.toLocaleLowerCase()));

  const handleDelete = async (typeId: string) => {
    if (!confirm(t('documentType.deleteConfirm'))) return;
    try {
      await store.DeleteDocumentType(typeId);
    } catch {
      // Error is handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header — the tab already names this page, so the heading is screen-reader only. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('documentType.list')}</h1>
        <nav className="text-sm">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('documentType.list')}</span>
        </nav>
        <Link to="/documents/types/new" className="sm:ml-auto">
          <Button>{t('documentType.create')}</Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
          </div>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('documentType.fieldDisplayName')}</TableHead>
              <TableHead>{t('documentType.fieldSlug')}</TableHead>
              <TableHead>{t('document.numberingField.active')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : visibleItems.length === 0 ? (
              <TableEmpty colSpan={4} message={t('documentType.noItems')} />
            ) : (
              visibleItems.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <Link
                      to={`/documents/types/${item.entity?.id || item.id}`}
                      className="text-sm font-medium text-primary hover:text-primary/80"
                    >
                      {String(item.display_name || item.slug || '')}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {String(item.slug ?? '')}
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? 'success' : 'secondary'}>
                      {item.is_active ? t('common.yes') : t('common.no')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/documents/types/${item.entity?.id || item.id}/edit`}>
                        <Button variant="ghost" size="sm">{t('common.edit')}</Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => handleDelete(item.entity?.id || item.id)}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default DocumentTypeListPage;
